/**
 * "Review hardcoded values" — the activity-level sweep, ADR-179 P2.
 *
 * Every literal in a supported slot that equals the resolved value of a published reference,
 * listed with the candidates that equal it, accepted one row at a time. The list is computed
 * by the canonical matcher; this component compares nothing, fetches nothing and decides
 * nothing about what a location is. It renders the answer and collects an acceptance.
 *
 * Every row starts unchecked and no control here checks more than one: ADR-127 D6a and its
 * 2026-09-10 carve-out set that boundary.
 *
 * WHY AN AMBIGUOUS ROW CANNOT BE ACCEPTED UNTIL IT IS RESOLVED
 * -----------------------------------------------------------
 * Several sets may carry the same value. Picking one for the operator would be the platform
 * answering "which set is this literal FROM?", which is exactly the question it cannot answer
 * and the reason the whole feature is a picker rather than an inference. So the choice is
 * shown, none is preselected, and the checkbox stays disabled until one is chosen.
 *
 * WHY A ZERO-CANDIDATE ROW IS STILL A ROW
 * ---------------------------------------
 * "No exact match found" is the feasibility answer the operator came for, and it is only
 * readable next to what was scanned — hence the coverage line naming the per-set key cap and
 * any data document that could not be read. A silent omission would read as "this literal is
 * fine", which is a different claim.
 */

import { useEffect, useMemo, useState } from 'react'

import type {
  DataReferenceMatch, DataReferenceMatchCandidate, DataReferenceMatchLocation,
  DataReferenceMatches,
} from '../api/client'
import { describeLocation, locationKey, type DataReferenceReplaceSelection } from './workflowDraft'
import { renderNote } from './DataReferencePicker'

export interface HardcodedValuesPanelProps {
  /** The matcher's answer for the current draft, or null when there is none to show. */
  matches: DataReferenceMatches | null
  /** Set when the matcher could not be asked. Rendered INSTEAD of rows: an empty sweep and a
   *  failed one are different facts, and only one of them says the draft is clean. */
  unavailableNote?: string | null
  /** A refused apply, in the host's words. Shown inline so the row it names is still on screen. */
  refusal?: string | null
  /** May be async: the host re-verifies through the endpoint before it writes, and the
   *  Apply button stays disabled for as long as the returned promise is pending. */
  onApply: (selections: DataReferenceReplaceSelection[]) => void | Promise<unknown>
  /** Click-through to the task a row sits in. */
  onSelectTask?: (task: string) => void
  onRefetch?: () => void
  /** Closes the panel. Optional: a host that owns the toggle elsewhere passes nothing. */
  onClose?: () => void
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** The same address formatter the draft mutators and the page use, with this panel's own
 *  separator — one definition, so the three cannot drift apart. */
function describeRow(loc: DataReferenceMatchLocation): string {
  return describeLocation(loc, ' › ')
}

function renderLiteral(literal: DataReferenceMatch['literal']): string {
  return typeof literal === 'string' ? `"${literal}"` : String(literal)
}

export function HardcodedValuesPanel(props: HardcodedValuesPanelProps) {
  const { matches, unavailableNote, refusal, onApply, onSelectTask, onRefetch, onClose } = props

  // Keyed by location, not by row index: a refetch can reorder or drop rows, and a selection
  // that silently moved to a different literal would apply somewhere the operator never looked.
  const [chosen, setChosen] = useState<Record<string, number>>({})
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  // The host re-verifies through the endpoint before it writes. A second click during that
  // await would start a second verify against the same captured draft.
  const [applying, setApplying] = useState(false)

  // A new matches object is a new answer about a possibly different draft. Carrying the ticks
  // across would let an operator apply a row they accepted against text that has since changed.
  useEffect(() => {
    setChosen({})
    setChecked({})
  }, [matches])

  const rows = useMemo(() => matches?.locations ?? [], [matches])

  const selections = useMemo(() => {
    const out: DataReferenceReplaceSelection[] = []
    for (const row of rows) {
      const key = locationKey(row.location)
      if (!checked[key]) continue
      const index = row.candidates.length === 1 ? 0 : chosen[key]
      const candidate: DataReferenceMatchCandidate | undefined =
        index === undefined ? undefined : row.candidates[index]
      if (!candidate) continue
      out.push({ kind: 'replace', location: row.location, literal: row.literal, candidate })
    }
    return out
  }, [rows, checked, chosen])

  if (unavailableNote) {
    return (
      <div data-testid="hardcoded-unavailable" role="status">
        <h3>Review hardcoded values</h3>
        <p>Hardcoded values could not be reviewed right now: {unavailableNote}</p>
        <p>
          Nothing is listed because nothing could be asked — not because this activity has no
          hardcoded values.
        </p>
        {onRefetch && (
          <button data-testid="hardcoded-refetch" onClick={onRefetch}>Try again</button>
        )}
      </div>
    )
  }

  const coverage = matches?.coverage
  const matched = rows.filter((row) => row.candidates.length > 0)
  const unmatched = rows.filter((row) => row.candidates.length === 0)

  /** One card per literal that has a candidate. The address is a click-through, the literal
   *  is what the operator recognises, and both the chip and the token are shown: the chip is
   *  the name a human reads, the token is what the apply writes. */
  const renderCard = (row: DataReferenceMatch) => {
    const key = locationKey(row.location)
    const single = row.candidates.length === 1
    const pickedIndex = single ? 0 : chosen[key]
    const acceptable = pickedIndex !== undefined
    return (
      <li key={key} data-testid={`hardcoded-row-${key}`}
          className="rounded border border-gray-200 bg-white p-2">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-0.5 shrink-0"
            data-testid={`hardcoded-check-${key}`}
            aria-label={`Accept ${describeRow(row.location)}`}
            disabled={!acceptable}
            checked={checked[key] === true}
            onChange={(e) =>
              setChecked((prev) => ({ ...prev, [key]: e.target.checked }))}
          />
          <div className="min-w-0 flex-1">
            <button
              type="button"
              data-testid={`hardcoded-task-${key}`}
              onClick={() => onSelectTask?.(row.location.task)}
              className="text-left font-medium text-gray-800 hover:underline"
              title="Open this task in the inspector"
            >
              {describeRow(row.location)}
            </button>
            <div className="mt-0.5 font-mono text-gray-900 break-all"
                 data-testid={`hardcoded-literal-${key}`}>
              {renderLiteral(row.literal)}
            </div>

            {single ? (
              <div className="mt-1 text-gray-600" data-testid={`hardcoded-candidate-${key}-0`}>
                <div>→ {row.candidates[0].chip}</div>
                <code className="block break-all text-[11px] text-gray-500">
                  {row.candidates[0].token}
                </code>
                {row.candidates[0].notes.length > 0 && (
                  <span data-testid={`hardcoded-notes-${key}-0`} className="text-amber-800">
                    ({row.candidates[0].notes.map(renderNote).join('; ')})
                  </span>
                )}
              </div>
            ) : (
              // Several sets carry this value. Which one this literal MEANS is the operator's
              // to say; the platform lists them and waits.
              <fieldset className="mt-1 text-gray-600">
                <legend className="text-amber-800">Several sets carry this value — choose one</legend>
                {/* Two versions of one set share an alias and therefore a token, so the
                    token is not an identity here — and it is not a label either, which is
                    why the pinned set is printed beside it. The choice itself is by INDEX,
                    which stays exact whatever the tokens say. */}
                {row.candidates.map((c, i) => (
                  <label key={`${c.token} ${c.pin.alias} ${c.pin.set}`}
                         className="mt-1 flex items-start gap-2">
                    <input
                      type="radio"
                      className="mt-0.5 shrink-0"
                      name={`hardcoded-choice-${key}`}
                      data-testid={`hardcoded-candidate-${key}-${i}`}
                      checked={chosen[key] === i}
                      onChange={() => setChosen((prev) => ({ ...prev, [key]: i }))}
                    />
                    <span className="min-w-0">
                      <span>→ {c.chip}</span>
                      <span data-testid={`hardcoded-candidate-set-${key}-${i}`}>
                        {' '}from {c.set}
                      </span>
                      <code className="block break-all text-[11px] text-gray-500">{c.token}</code>
                      {c.notes.length > 0 && (
                        <span data-testid={`hardcoded-notes-${key}-${i}`} className="text-amber-800">
                          ({c.notes.map(renderNote).join('; ')})
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
          </div>
        </div>
      </li>
    )
  }

  return (
    <div data-testid="hardcoded-values-panel" className="text-xs">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-gray-900">Review hardcoded values</h3>
          {rows.length > 0 && (
            <p data-testid="hardcoded-summary" className="text-gray-600">
              {matched.length} of {rows.length} literals have a published equivalent
            </p>
          )}
        </div>
        {onClose && (
          <button
            type="button"
            data-testid="hardcoded-close"
            onClick={onClose}
            className="rounded px-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
            title="Close"
          >
            ✕
          </button>
        )}
      </div>

      {refusal && (
        <p data-testid="hardcoded-refusal" role="alert" className="mt-2 text-amber-800">{refusal}</p>
      )}

      {rows.length === 0 && (
        <p data-testid="hardcoded-empty" className="mt-2 text-gray-600">
          No literal in a scanned slot of this activity.
        </p>
      )}

      {matched.length > 0 && (
        <ul className="mt-2 space-y-2">{matched.map(renderCard)}</ul>
      )}

      {rows.length > 0 && (
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            data-testid="hardcoded-apply"
            disabled={selections.length === 0 || applying}
            onClick={() => {
              const done = onApply(selections)
              if (!done || typeof (done as Promise<unknown>).then !== 'function') return
              setApplying(true)
              void (done as Promise<unknown>).finally(() => setApplying(false))
            }}
            className="rounded bg-indigo-600 px-2.5 py-1 font-medium text-white disabled:bg-gray-200 disabled:text-gray-500"
          >
            {applying ? 'Applying…' : `Apply ${selections.length} selected`}
          </button>
          {onRefetch && (
            <button type="button" data-testid="hardcoded-refetch" onClick={onRefetch}
                    className="text-gray-600 hover:underline">
              Refresh
            </button>
          )}
        </div>
      )}

      {unmatched.length > 0 && (
        <details data-testid="hardcoded-nomatch-fold" className="mt-3">
          <summary className="cursor-pointer text-gray-600">
            {plural(unmatched.length, 'literal', 'literals')} with no match in{' '}
            {plural(coverage?.sets_scanned ?? 0, 'set', 'sets')}
          </summary>
          <ul className="mt-1 space-y-1 text-gray-600">
            {unmatched.map((row) => {
              const key = locationKey(row.location)
              return (
                <li key={key} data-testid={`hardcoded-row-${key}`} className="min-w-0">
                  <button
                    type="button"
                    data-testid={`hardcoded-task-${key}`}
                    onClick={() => onSelectTask?.(row.location.task)}
                    className="text-left hover:underline"
                  >
                    {describeRow(row.location)}
                  </button>
                  {' '}
                  <code data-testid={`hardcoded-literal-${key}`} className="break-all">
                    {renderLiteral(row.literal)}
                  </code>
                  <p data-testid={`hardcoded-nomatch-${key}`} className="sr-only">
                    No exact match found ({coverage?.sets_scanned ?? 0} sets scanned)
                  </p>
                </li>
              )
            })}
          </ul>
        </details>
      )}

      {rows.length === 0 && onRefetch && (
        <button type="button" data-testid="hardcoded-refetch" onClick={onRefetch}
                className="mt-2 text-gray-600 hover:underline">
          Refresh
        </button>
      )}

      {coverage && (
        <p data-testid="hardcoded-coverage" className="mt-3 text-[11px] text-gray-500">
          Scanned {coverage.sets_scanned} data{' '}
          {coverage.sets_scanned === 1 ? 'set' : 'sets'}, up to {coverage.key_limit} keys each,
          across {(matches?.slots_scanned ?? []).join(', ') || 'no slots'}.
          {coverage.candidates_dropped > 0 && (
            <> {coverage.candidates_dropped} value-equal{' '}
              {coverage.candidates_dropped === 1 ? 'reference was' : 'references were'} dropped
              because the author-time checks would refuse them.</>
          )}
          {coverage.key_limit_hit.length > 0 && (
            <> Past that cap, so not fully scanned: {coverage.key_limit_hit.join(', ')}.</>
          )}
          {coverage.sets_skipped.length > 0 && (
            <> Not read at all: {coverage.sets_skipped
              .map((s) => `${s.path} (${s.reason})`).join(', ')}.</>
          )}
        </p>
      )}
    </div>
  )
}
